import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home,
  Megaphone,
  CreditCard,
  BarChart3,
  HelpCircle,
  FileText,
  LogOut,
  Menu,
  X,
  User,
  PlusCircle
} from 'lucide-react';

// Import page components
import DashboardStart from './dashboard/DashboardStart';
import DashboardAnzeigen from './dashboard/DashboardAnzeigen';
import DashboardBudget from './dashboard/DashboardBudget';
import DashboardErgebnisse from './dashboard/DashboardErgebnisse';
import DashboardHilfe from './dashboard/DashboardHilfe';
import DashboardRechnungen from './dashboard/DashboardRechnungen';
import AccountSettingsModal from '../components/AccountSettingsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { id: 'start', label: 'Start', icon: Home },
  { id: 'new-campaign', label: 'Neue Kampagne', icon: PlusCircle, isLink: true, href: '/new-campaign' },
  { id: 'anzeigen', label: 'Anzeigen', icon: Megaphone },
  { id: 'budget', label: 'Budget', icon: CreditCard },
  { id: 'ergebnisse', label: 'Ergebnisse', icon: BarChart3 },
  { id: 'rechnungen', label: 'Rechnungen', icon: FileText },
];

const DashboardNew = ({ user }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('start');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  useEffect(() => {
    fetchUserData();
    
    // Push dashboard_active event to dataLayer for GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'dashboard_active'
    });
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserData(response.data);
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Fehler beim Laden der Benutzerdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Erfolgreich abgemeldet');
  };

  const handleMenuClick = (viewId) => {
    setActiveView(viewId);
    setMobileMenuOpen(false);
  };

  const renderActiveView = () => {
    switch (activeView) {
      case 'start':
        return <DashboardStart userData={userData} onNavigate={handleMenuClick} />;
      case 'anzeigen':
        return <DashboardAnzeigen />;
      case 'budget':
        return <DashboardBudget />;
      case 'ergebnisse':
        return <DashboardErgebnisse />;
      case 'rechnungen':
        return <DashboardRechnungen />;
      case 'hilfe':
        return <DashboardHilfe />;
      default:
        return <DashboardStart userData={userData} onNavigate={handleMenuClick} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md border border-gray-200"
      >
        {mobileMenuOpen ? (
          <X className="w-6 h-6 text-gray-700" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700" />
        )}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${mobileMenuOpen ? 'block' : 'hidden'}
          lg:block
          fixed lg:sticky
          top-0 left-0 lg:left-auto
          h-screen z-40
          w-[240px] bg-white
          transition-all duration-300 ease-in-out
          rounded-r-[28px]
          flex flex-col
          overflow-hidden
          sidebar-gpu
        `}
      >
        {/* Logo */}
        <div className="p-6 pb-2">
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="AdSimplify Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold text-gray-900">
              AdSimplify
            </span>
          </div>
        </div>

        {/* Made in Österreich */}
        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-gray-500">
            Made with ❤️ in Österreich.
          </p>
        </div>

        {/* Navigation */}
        <nav className="px-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;

            // Wenn es ein externer Link ist (neue Kampagne)
            if (item.isLink) {
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.href)}
                  className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-xl
                    transition-all duration-200
                    bg-[#0040FF] text-white hover:bg-[#0033CC]
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold text-[15px] text-sharp">{item.label}</span>
                </button>
              );
            }

            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-xl
                  transition-all duration-200
                  ${
                    isActive
                      ? 'bg-[#E6EDFF] text-[#0040FF]'
                      : 'text-black hover:bg-gray-200/50'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-semibold text-[15px] text-sharp">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Spacer to push bottom items down */}
        <div className="flex-1"></div>

        {/* Bottom Section: Support, Profil, Logout - All together at bottom */}
        <div className="mt-auto">
          {/* Support Section */}
          <div className="p-4 pt-8 relative">
            <div className="relative">
              {/* Profile Images - Overlapping at top of container */}
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 flex items-center -space-x-2 z-10">
                <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 overflow-hidden shadow-sm">
                  <img
                    src="https://customer-assets.emergentagent.com/job_campaign-hub-84/artifacts/ebssqehq_1.png"
                    alt="Support Team 1"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 overflow-hidden shadow-sm">
                  <img
                    src="https://customer-assets.emergentagent.com/job_campaign-hub-84/artifacts/4jwj7zyu_2.png"
                    alt="Support Team 2"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <button
                onClick={() => handleMenuClick('hilfe')}
                className="w-full pt-8 pb-4 px-4 hover:bg-gray-100 transition-all duration-200 cursor-pointer"
                style={{
                  borderLeft: '1px solid #D1D5DB',
                  borderRight: '1px solid #D1D5DB',
                  borderBottom: '1px solid #D1D5DB',
                  borderRadius: '12px'
                }}
              >
                {/* Support Text */}
                <div className="text-center">
                  <p className="text-sm text-black font-bold text-sharp">Fragen?</p>
                  <p className="text-sm text-black font-normal text-sharp">Wir sind für dich da!</p>
                </div>
              </button>
            </div>
          </div>

          {/* Profil Button */}
          <div className="px-4 pb-2">
            <button
              onClick={() => setShowAccountSettings(true)}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-black hover:bg-gray-200/50 transition-all duration-200"
            >
              <User className="w-5 h-5" />
              <span className="font-semibold text-[15px] text-sharp">Profil</span>
            </button>
          </div>

          {/* Logout Button - with bottom padding on button itself */}
          <div className="px-4">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 pb-4 rounded-xl text-black hover:bg-gray-200/50 transition-all duration-200"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold text-[15px] text-sharp">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 lg:p-8">
          {renderActiveView()}
        </div>
      </main>

      {/* Account Settings Modal */}
      <AccountSettingsModal 
        isOpen={showAccountSettings} 
        onClose={() => setShowAccountSettings(false)} 
      />
    </div>
  );
};

export default DashboardNew;
